/**
 * Agent Layout Component
 *
 * Simplified layout for agents using the same SidebarProvider pattern.
 * Agents have: Dashboard + Conversations navigation only.
 * Includes agent profile, status indicator, and logout.
 */

import React, { useState, useEffect } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  MessageSquare,
  LogOut,
  Circle,
  ChevronsUpDown,
  Bot,
} from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'
import { logger } from '../../utils/logger'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
} from '../ui/sidebar'
import { Avatar, AvatarFallback } from '../ui/Avatar'
import { ThemeToggle } from '../ui/ThemeToggle'
import { SidebarTrigger } from '../ui/sidebar'
import { Separator } from '../ui/Separator'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/ShadcnDropdownMenu'

interface AgentProfile {
  agent_name: string
  agent_role: string
  team_name: string
  status: string
  is_online: boolean
  current_chat_count: number
  max_concurrent_chats: number
}

const navigationItems = [
  { path: '/agent/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/agent/conversations', icon: MessageSquare, label: 'Conversations' },
]

function getStatusColor(status: string) {
  switch (status) {
    case 'online': return 'text-green-500'
    case 'away': return 'text-yellow-500'
    case 'busy': return 'text-red-500'
    default: return 'text-muted-foreground'
  }
}

export const AgentLayout: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [agentProfile, setAgentProfile] = useState<AgentProfile | null>(null)

  useEffect(() => {
    const fetchAgentProfile = async () => {
      try {
        const response = await api.get('/agents/auth/profile')
        setAgentProfile(response.data)
      } catch (error) {
        logger.error('Failed to fetch agent profile', error)
      }
    }
    fetchAgentProfile()
  }, [])

  const handleLogout = async () => {
    try {
      await api.post('/agents/auth/logout')
      logout()
      navigate('/agent/login')
    } catch {
      logout()
      navigate('/agent/login')
    }
  }

  const agentInitial = agentProfile?.agent_name?.charAt(0)?.toUpperCase() || 'A'

  return (
    <SidebarProvider>
      <Sidebar collapsible="icon">
        {/* Logo */}
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" asChild tooltip="Agent Portal">
                <NavLink to="/agent/dashboard">
                  <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                    <Bot className="size-4" />
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">Agent Portal</span>
                    <span className="truncate text-xs text-sidebar-foreground/60">Chat Management</span>
                  </div>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        {/* Navigation */}
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>Navigation</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {navigationItems.map((item) => {
                  const isActive = location.pathname === item.path
                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        tooltip={item.label}
                      >
                        <NavLink to={item.path}>
                          <item.icon />
                          <span>{item.label}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        {/* Agent profile footer */}
        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                    tooltip={agentProfile?.agent_name || 'Agent'}
                  >
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                        {agentInitial}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold flex items-center gap-1.5">
                        {agentProfile?.agent_name || 'Agent'}
                        {agentProfile && (
                          <Circle className={`size-2 fill-current ${getStatusColor(agentProfile.status)}`} />
                        )}
                      </span>
                      <span className="truncate text-xs">
                        {agentProfile?.team_name || 'No team'}
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-auto size-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                  side="bottom"
                  align="end"
                  sideOffset={4}
                >
                  <DropdownMenuLabel className="p-0 font-normal">
                    <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                      <Avatar className="h-8 w-8 rounded-lg">
                        <AvatarFallback className="rounded-lg">
                          {agentInitial}
                        </AvatarFallback>
                      </Avatar>
                      <div className="grid flex-1 text-left text-sm leading-tight">
                        <span className="truncate font-semibold">
                          {agentProfile?.agent_name || 'Agent'}
                        </span>
                        <span className="truncate text-xs">{agentProfile?.agent_role}</span>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 size-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>

      <SidebarInset>
        {/* Agent header */}
        <header className="sticky top-0 z-50 flex h-14 shrink-0 items-center gap-2 border-b bg-background/80 backdrop-blur-md px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <span className="text-sm font-medium text-foreground">Agent Portal</span>
          <div className="flex-1" />
          <ThemeToggle />
        </header>
        {/* Page content */}
        <div className="flex-1 overflow-hidden">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}

export default AgentLayout
